import { CommerceStatusBadge } from "@/components/commerce/commerce-status-badge";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DeliveryService } from "@/gen/omnios/v1/delivery_connect";
import { useEscapeBack } from "@/hooks/use-escape-back";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { createClient } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
	ArrowLeft,
	Ban,
	Download,
	ExternalLink,
	Loader2,
	MapPin,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/commerce/shipments/$id")({
	component: ShipmentDetailPage,
});

const client = createClient(DeliveryService, transport);

/** Semantic dot colors for the tracking timeline. */
const TIMELINE_DOT_COLORS: Record<string, string> = {
	created: "bg-muted-foreground",
	picked_up: "bg-blue-500",
	in_transit: "bg-amber-500",
	out_for_delivery: "bg-blue-500",
	delivered: "bg-green-500",
	returned: "bg-red-500",
	failed: "bg-red-500",
	cancelled: "bg-muted-foreground",
};

const NON_CANCELLABLE = new Set(["delivered", "cancelled", "failed"]);

function ShipmentDetailPage() {
	const { t } = useTranslation("commerce");
	const { id } = Route.useParams();
	const qc = useQueryClient();
	useEscapeBack("/commerce/shipments");
	const [showCancel, setShowCancel] = useState(false);

	const {
		data: shipment,
		isLoading,
		error,
	} = useQuery({
		queryKey: ["shipment", id],
		queryFn: () => client.getShipment({ id }),
	});

	// Track (refresh from provider)
	const trackMut = useMutation({
		mutationFn: () => client.trackShipment({ shipmentId: id }),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["shipment", id] }),
	});

	// Cancel
	const cancelMut = useMutation({
		mutationFn: () => client.cancelShipment({ shipmentId: id }),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["shipment", id] });
			qc.invalidateQueries({ queryKey: ["shipments"] });
			setShowCancel(false);
		},
	});

	useBreadcrumbs([
		{ label: t("shipments_title"), href: "/commerce/shipments" },
		{ label: shipment?.trackingNumber || "..." },
	]);

	const fmtDate = (ts?: { seconds: bigint }) =>
		ts ? new Date(Number(ts.seconds) * 1000).toLocaleDateString("ru-RU") : "—";

	const fmtDateTime = (ts?: { seconds: bigint }) =>
		ts ? new Date(Number(ts.seconds) * 1000).toLocaleString("ru-RU") : "—";

	if (isLoading) {
		return (
			<p className="py-8 text-center text-sm text-muted-foreground">
				{t("common:loading", { defaultValue: "Загрузка..." })}
			</p>
		);
	}

	if (error || !shipment) {
		return (
			<div className="py-16 text-center text-sm text-muted-foreground">
				{t("shipment_not_found", { defaultValue: "Отгрузка не найдена" })}
			</div>
		);
	}

	const canCancel = !NON_CANCELLABLE.has(shipment.status);

	// Extract address fields from Struct
	const extractAddr = (s?: {
		fields?: Record<string, { kind: { value: unknown } }>;
	}) => {
		if (!s?.fields) return null;
		const get = (k: string) => {
			const f = s.fields?.[k];
			if (!f) return "";
			const v = f.kind;
			if (v && typeof v === "object" && "value" in v) return String(v.value);
			return "";
		};
		return {
			country: get("country"),
			city: get("city"),
			street: get("street"),
			postalCode: get("postal_code") || get("postalCode"),
		};
	};

	const origin = extractAddr(shipment.origin as any);
	const destination = extractAddr(shipment.destination as any);

	// Events sorted most recent first
	const events = [...shipment.events].sort((a, b) => {
		const ta = a.createdAt ? Number(a.createdAt.seconds) : 0;
		const tb = b.createdAt ? Number(b.createdAt.seconds) : 0;
		return tb - ta;
	});

	return (
		<div className="space-y-6">
			<div className="flex items-center gap-2 px-4 py-2 shrink-0 justify-end">
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() => trackMut.mutate()}
				>
					{t("menu_refresh")}
				</Button>
			</div>
			{/* Header */}
			<div className="flex items-center gap-3">
				<Link
					to="/commerce/shipments"
					className="rounded-md p-1.5 text-muted-foreground hover:bg-accent"
				>
					<ArrowLeft className="h-4 w-4" />
				</Link>
				<div className="flex-1 min-w-0">
					<div className="flex items-center gap-2">
						<span className="text-lg font-semibold">
							{shipment.trackingNumber || shipment.id.slice(0, 12)}
						</span>
						<CommerceStatusBadge entity="shipment" status={shipment.status} />
					</div>
				</div>
				{canCancel && (
					<Button
						variant="destructive"
						size="sm"
						onClick={() => setShowCancel(true)}
					>
						<Ban className="h-4 w-4 mr-1" />
						{t("cancel_shipment")}
					</Button>
				)}
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
				{/* Left — timeline */}
				<div className="lg:col-span-2 space-y-4">
					{/* Tracking links */}
					{(shipment.trackingUrl || shipment.labelUrl) && (
						<div className="flex items-center gap-3">
							{shipment.trackingUrl && (
								<a
									href={shipment.trackingUrl}
									target="_blank"
									rel="noopener noreferrer"
									className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
								>
									<ExternalLink className="h-3.5 w-3.5" />
									{t("tracking_link")}
								</a>
							)}
							{shipment.labelUrl && (
								<a
									href={shipment.labelUrl}
									target="_blank"
									rel="noopener noreferrer"
									className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
								>
									<Download className="h-3.5 w-3.5" />
									{t("label_download")}
								</a>
							)}
						</div>
					)}

					{/* Timeline */}
					<Card>
						<CardHeader className="py-3">
							<CardTitle className="text-sm">
								{t("tracking_timeline")}
							</CardTitle>
						</CardHeader>
						<CardContent>
							{events.length === 0 ? (
								<p className="text-sm text-muted-foreground py-4 text-center">
									{t("no_events", {
										defaultValue: "Нет событий",
									})}
								</p>
							) : (
								<div className="relative space-y-0">
									{events.map((evt, i) => {
										const dotClass =
											i === 0
												? (TIMELINE_DOT_COLORS[evt.status] ??
													"bg-muted-foreground")
												: "bg-border";
										return (
											<div key={evt.id} className="flex gap-3 pb-4 last:pb-0">
												{/* Dot + line */}
												<div className="flex flex-col items-center">
													<div
														className={`h-3 w-3 rounded-full shrink-0 mt-0.5 ${dotClass}`}
													/>
													{i < events.length - 1 && (
														<div className="w-px flex-1 bg-border mt-1" />
													)}
												</div>
												{/* Content */}
												<div className="flex-1 min-w-0">
													<div className="flex items-center gap-2">
														<span className="text-sm font-medium">
															{t(`status_${evt.status}`, {
																defaultValue: evt.status,
															})}
														</span>
														<span className="text-xs text-muted-foreground">
															{fmtDateTime(evt.createdAt)}
														</span>
													</div>
													{evt.description && (
														<p className="text-sm text-muted-foreground mt-0.5">
															{evt.description}
														</p>
													)}
													{evt.location && (
														<p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
															<MapPin className="h-3 w-3" />
															{evt.location}
														</p>
													)}
												</div>
											</div>
										);
									})}
								</div>
							)}
						</CardContent>
					</Card>
				</div>

				{/* Right sidebar */}
				<aside className="space-y-4">
					{/* Order & method */}
					<Card>
						<CardHeader className="py-3">
							<CardTitle className="text-sm">
								{t("shipment_info", { defaultValue: "Информация" })}
							</CardTitle>
						</CardHeader>
						<CardContent className="space-y-2 text-sm">
							<div className="flex justify-between">
								<span className="text-muted-foreground">
									{t("pay_col_order")}
								</span>
								<Link
									to="/commerce/orders/$id"
									params={{ id: shipment.orderId }}
									className="text-primary hover:underline font-mono text-xs"
								>
									{shipment.orderId.slice(0, 8)}
								</Link>
							</div>
							{shipment.providerShipmentId && (
								<div className="flex justify-between">
									<span className="text-muted-foreground">{t("provider")}</span>
									<span className="font-mono text-xs">
										{shipment.providerShipmentId}
									</span>
								</div>
							)}
						</CardContent>
					</Card>

					{/* Origin */}
					{origin && (
						<Card>
							<CardHeader className="py-3">
								<CardTitle className="text-sm">{t("origin")}</CardTitle>
							</CardHeader>
							<CardContent className="text-sm text-muted-foreground space-y-1">
								{origin.country && <p>{origin.country}</p>}
								{origin.city && <p>{origin.city}</p>}
								{origin.street && <p>{origin.street}</p>}
								{origin.postalCode && <p>{origin.postalCode}</p>}
							</CardContent>
						</Card>
					)}

					{/* Destination */}
					{destination && (
						<Card>
							<CardHeader className="py-3">
								<CardTitle className="text-sm">{t("destination")}</CardTitle>
							</CardHeader>
							<CardContent className="text-sm text-muted-foreground space-y-1">
								{destination.country && <p>{destination.country}</p>}
								{destination.city && <p>{destination.city}</p>}
								{destination.street && <p>{destination.street}</p>}
								{destination.postalCode && <p>{destination.postalCode}</p>}
							</CardContent>
						</Card>
					)}

					{/* Dates */}
					<Card>
						<CardHeader className="py-3">
							<CardTitle className="text-sm">{t("shipment_dates")}</CardTitle>
						</CardHeader>
						<CardContent className="space-y-2 text-sm">
							<div className="flex justify-between">
								<span className="text-muted-foreground">{t("date_col")}</span>
								<span>{fmtDate(shipment.createdAt)}</span>
							</div>
							<div className="flex justify-between">
								<span className="text-muted-foreground">{t("shipped_at")}</span>
								<span>{fmtDate(shipment.shippedAt)}</span>
							</div>
							<div className="flex justify-between">
								<span className="text-muted-foreground">
									{t("est_delivery")}
								</span>
								<span>{fmtDate(shipment.estimatedDelivery)}</span>
							</div>
							<div className="flex justify-between">
								<span className="text-muted-foreground">
									{t("delivered_at")}
								</span>
								<span>{fmtDate(shipment.deliveredAt)}</span>
							</div>
						</CardContent>
					</Card>
				</aside>
			</div>

			{/* Cancel confirmation */}
			<AlertDialog open={showCancel} onOpenChange={setShowCancel}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t("cancel_shipment")}</AlertDialogTitle>
						<AlertDialogDescription>
							{t("cancel_confirm")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>
							{t("common:cancel", { defaultValue: "Отмена" })}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => cancelMut.mutate()}
							className="bg-destructive text-destructive-foreground"
							disabled={cancelMut.isPending}
						>
							{cancelMut.isPending && (
								<Loader2 className="h-4 w-4 mr-1 animate-spin" />
							)}
							{t("cancel_shipment")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
